import React, { forwardRef } from 'react';
import { useTenantProfile } from '@hooks';
import { INK } from '../Theme/palette';

/**
 * Shared frame of every printed document (estimate, job details, inspection, ...).
 *
 * The page margin is 0 on purpose: any non-zero @page margin makes the browser print its own
 * header and footer (date, page title, URL) into it. The 12-14mm of white space is padding
 * instead, cloned onto every page fragment so a document that runs onto a second page keeps it.
 */
export const PrintSheet = forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => (
    <div
      ref={ref}
      style={{
        padding: '14mm',
        color: '#000',
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
      }}
    >
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
        }
      `}</style>
      {children}
    </div>
  )
);

PrintSheet.displayName = 'PrintSheet';

interface IPrintHeaderProps {
  title: string;
  subtitle?: string;
  customerName?: string;
  registrationNumber?: string;
  vehicleModel?: string;
  mileage?: number;
  /** The business VAT number, for documents that carry prices (estimate). */
  showVat?: boolean;
}

/**
 * Same header on every printed document: the workshop's logo and contact details on top,
 * then title + subtitle on the left and vehicle details and date on the right.
 * The workshop comes from the tenant profile store, so callers do not pass it in.
 */
export const PrintHeader: React.FC<IPrintHeaderProps> = ({
  title,
  subtitle,
  customerName,
  registrationNumber,
  vehicleModel,
  mileage,
  showVat,
}) => {
  const { tenantProfile } = useTenantProfile();
  const logo = tenantProfile?.logo;
  const businessName = tenantProfile?.tenant?.name;
  const addressLines = [
    tenantProfile?.address,
    tenantProfile?.address2,
    [tenantProfile?.city, tenantProfile?.zipCode].filter(Boolean).join(' '),
  ].filter((line) => line && line.trim());
  const contactLines = [
    tenantProfile?.companyPhone,
    tenantProfile?.companyEmail,
    showVat && tenantProfile?.vatNumber ? `VAT: ${tenantProfile.vatNumber}` : undefined,
  ].filter(Boolean);
  const hasBusinessDetails = !!businessName || addressLines.length > 0 || contactLines.length > 0;

  return (
    <>
      {(logo || hasBusinessDetails) && (
        <div
          style={{
            display: 'flex',
            justifyContent: logo ? 'space-between' : 'flex-start',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 12,
          }}
        >
          {logo && (
            <img
              src={logo}
              alt={businessName ? `${businessName} logo` : 'Business logo'}
              style={{ maxHeight: 70, maxWidth: 220, objectFit: 'contain', display: 'block' }}
            />
          )}
          {hasBusinessDetails && (
            <div style={{ textAlign: logo ? 'right' : 'left', fontSize: 11, lineHeight: 1.4, color: '#333' }}>
              {businessName && <div style={{ fontWeight: 700, color: INK }}>{businessName}</div>}
              {addressLines.map((line, i) => (
                <div key={`addr-${i}`}>{line}</div>
              ))}
              {contactLines.map((line, i) => (
                <div key={`contact-${i}`}>{line}</div>
              ))}
            </div>
          )}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: `2px solid ${INK}`,
          paddingBottom: 8,
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12 }}>{subtitle}</div>}
        </div>
        <div style={{ textAlign: 'right', fontSize: 12 }}>
          {customerName && (
            <div>
              <strong>Customer:</strong> {customerName}
            </div>
          )}
          {registrationNumber && (
            <div>
              <strong>Reg:</strong> {registrationNumber}
            </div>
          )}
          {vehicleModel && (
            <div>
              <strong>Vehicle:</strong> {vehicleModel}
            </div>
          )}
          {mileage != null && (
            <div>
              <strong>Mileage:</strong> {mileage.toLocaleString('en-GB')}
            </div>
          )}
          <div>
            <strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}
          </div>
        </div>
      </div>
    </>
  );
};
